/* ─────────────────────────────────────────────────────────────
   Medicore product page additions

   Two jobs on the assurance panel:

     1. estimated arrival dates, worked out against the store's
        clock rather than the visitor's
     2. keeping the free-shipping progress and the bulk tiers in
        step with the cart

   Both are enhancements. With this script switched off the panel
   still states the terms; it just does not personalise them.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var DAY = 86400000;

  function panels() {
    return document.querySelectorAll('[data-mc-assure]');
  }

  /* ── the store's clock ─────────────────────────────────────
     Liquid renders a timezone offset, not a time. Section markup is
     cached, so a server-rendered clock can be hours stale, while an
     offset is a property of the store and does not move. Applying it
     to the browser's own "now" gives the real local time in the
     store's timezone, whatever the visitor's device is set to.
     ─────────────────────────────────────────────────────────── */

  function offsetMinutes(raw) {
    // "+0530" / "-0400"
    var match = /^([+-])(\d{2})(\d{2})$/.exec((raw || '').trim());
    if (!match) return null;

    var minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
    return match[1] === '-' ? -minutes : minutes;
  }

  function storeNow(panel) {
    var offset = offsetMinutes(panel.dataset.tzOffset);
    if (offset === null) return null;

    // Shift real UTC into store-local, then read the result with the
    // UTC getters so the device's own zone never enters into it.
    return new Date(Date.now() + offset * 60000);
  }

  function isWeekend(date) {
    var day = date.getUTCDay();
    return day === 0 || day === 6;
  }

  function addDeliveryDays(from, days, skipWeekends) {
    var date = new Date(from.getTime());
    var left = days;

    // Zero days means "arrives the day it ships", so the start date
    // still has to be a working day.
    if (left === 0) {
      while (skipWeekends && isWeekend(date)) {
        date = new Date(date.getTime() + DAY);
      }
      return date;
    }

    while (left > 0) {
      date = new Date(date.getTime() + DAY);
      if (!skipWeekends || !isWeekend(date)) left -= 1;
    }
    return date;
  }

  function formatDate(date, locale) {
    try {
      return date.toLocaleDateString(locale || undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
      });
    } catch (error) {
      return date.toUTCString().slice(0, 11);
    }
  }

  function renderDelivery(panel) {
    var eta = panel.querySelector('[data-mc-delivery]');
    var etaText = panel.querySelector('[data-mc-delivery-text]');
    var cutoffLine = panel.querySelector('[data-mc-cutoff]');
    var cutoffText = panel.querySelector('[data-mc-cutoff-text]');
    if (!eta || !etaText) return;

    var now = storeNow(panel);
    if (!now) return;

    var cutoff = parseInt(panel.dataset.cutoffHour, 10);
    var minDays = parseInt(panel.dataset.daysMin, 10);
    var maxDays = parseInt(panel.dataset.daysMax, 10);
    var skipWeekends = panel.dataset.skipWeekends === 'true';

    if (isNaN(cutoff) || isNaN(minDays) || isNaN(maxDays)) return;

    // Dispatch day: today if we are inside the cut-off and it is a
    // working day, otherwise the next working day.
    var dispatch = new Date(now.getTime());
    var missedCutoff = now.getUTCHours() >= cutoff;

    if (missedCutoff) dispatch = new Date(dispatch.getTime() + DAY);
    while (skipWeekends && isWeekend(dispatch)) {
      dispatch = new Date(dispatch.getTime() + DAY);
    }

    var locale = document.documentElement.lang || undefined;
    var earliest = formatDate(addDeliveryDays(dispatch, minDays, skipWeekends), locale);
    var latest = formatDate(addDeliveryDays(dispatch, maxDays, skipWeekends), locale);

    var arrival = earliest === latest
      ? '<strong>' + earliest + '</strong>'
      : '<strong>' + earliest + '</strong> &ndash; <strong>' + latest + '</strong>';

    etaText.innerHTML = (panel.dataset.arrivalLabel || 'Arrives') + ' ' + arrival;
    eta.hidden = false;

    // The countdown only exists while it is still true. Past the
    // cut-off it would be a promise the warehouse cannot keep, so the
    // line goes entirely - the dates above have already moved to
    // tomorrow's dispatch and say the rest.
    if (!cutoffLine || !cutoffText) return;

    var remaining = missedCutoff ? '' : countdownText(now, cutoff);

    if (!remaining || cutoffLine.dataset.showCountdown === 'false') {
      cutoffLine.hidden = true;
      return;
    }

    cutoffText.innerHTML = (cutoffLine.dataset.cutoffLabel || 'Order within [time] for same-day dispatch')
      .replace('[time]', '<strong>' + remaining + '</strong>');
    cutoffLine.hidden = false;
  }

  /* Whole minutes only. A ticking seconds counter on a supply store
     reads as a sales gimmick; hours and minutes read as logistics. */
  function countdownText(now, cutoffHour) {
    var minutesLeft = (cutoffHour * 60) - (now.getUTCHours() * 60 + now.getUTCMinutes());
    if (minutesLeft <= 0) return '';

    var hours = Math.floor(minutesLeft / 60);
    var minutes = minutesLeft % 60;

    if (hours && minutes) return hours + 'h ' + minutes + 'm';
    if (hours) return hours + 'h';
    return minutes + 'm';
  }

  /* ── cart-aware rows ───────────────────────────────────────
     The panel renders with the cart total Liquid knew about. After
     an add to cart that number is stale, so it is re-read from
     /cart.js - the one place that is always right.
     ─────────────────────────────────────────────────────────── */

  function money(cents) {
    var formatted = (cents / 100).toFixed(2);
    // Trailing ".00" is noise on a threshold like $250.
    return formatted.replace(/\.00$/, '');
  }

  /* Liquid hands over the cart's own symbol. Reading it back out of
     rendered text worked until the "qualified" wording replaced the
     only amount on screen. */
  function currencySymbol(panel) {
    return panel.dataset.currency || '$';
  }

  function renderCart(panel, total) {
    panel.dataset.cartTotal = total;

    var threshold = parseInt(panel.dataset.threshold, 10) || 0;
    var note = panel.querySelector('[data-mc-shipping-note]');
    var meter = panel.querySelector('[data-mc-meter]');
    var bar = panel.querySelector('[data-mc-bar]');

    if (threshold > 0) {
      var qualified = total >= threshold;
      var progress = Math.min(100, Math.round((total / threshold) * 100));
      var short = currencySymbol(panel) + money(threshold - total);

      // An empty cart has nothing to measure, so the meter goes rather
      // than sitting there reading zero. It comes back on the first
      // add to cart and leaves again if the cart is emptied.
      if (meter) meter.hidden = total <= 0;

      var knob = panel.querySelector('[data-mc-knob]');

      if (bar) bar.style.width = progress + '%';
      if (knob) knob.style.left = progress + '%';

      if (note) {
        if (qualified) {
          note.textContent = note.dataset.qualified || note.textContent;
        } else if (total > 0 && note.dataset.remaining) {
          note.textContent = note.dataset.remaining.replace('[amount]', short);
        } else if (note.dataset.flat) {
          // Back to the standing offer once there is no progress to
          // report - otherwise the note keeps quoting a shortfall
          // against a cart that no longer exists.
          note.textContent = note.dataset.flat;
        }
      }
    }

    ['1', '2'].forEach(function (index) {
      var tier = panel.querySelector('[data-mc-tier="' + index + '"]');
      if (!tier) return;

      var spend = parseInt(panel.dataset['tier' + index], 10) || 0;
      tier.classList.toggle('is-reached', spend > 0 && total >= spend);
    });
  }

  function refreshCart() {
    var list = panels();
    if (!list.length) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status + ' from cart.js');
        return response.json();
      })
      .then(function (cart) {
        Array.prototype.forEach.call(list, function (panel) {
          renderCart(panel, cart.total_price);
        });
      })
      .catch(function (error) {
        // A stale total is a cosmetic problem; the terms are still
        // correct and the cart itself is authoritative.
        console.error('Assurance panel could not read the cart:', error);
      });
  }

  function init() {
    Array.prototype.forEach.call(panels(), function (panel) {
      if (panel.dataset.mcAssureBound === 'true') return;
      panel.dataset.mcAssureBound = 'true';

      renderDelivery(panel);
    });
  }

  // Re-rendered every minute so the countdown stays true and the
  // dates roll over correctly for anyone who leaves the tab open.
  window.setInterval(function () {
    Array.prototype.forEach.call(panels(), renderDelivery);
  }, 60000);

  document.addEventListener('cart:added', refreshCart);
  document.addEventListener('cart:updated', refreshCart);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ─────────────────────────────────────────────────────────────
   Product tabs

   A disclosure, not an ARIA tablist. The same buttons are a tab
   strip on desktop and an accordion on mobile, and swapping roles at
   a breakpoint is worse for a screen reader than picking the pattern
   that is true in both: a button that expands a region.

   One panel open at a time in either mode - two open panels in a tab
   strip would be nonsense, and on mobile it keeps the page short.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* Below this width the strip is an accordion, and an accordion
     that force-opens a panel is just a heading with extra steps. */
  var collapsed = window.matchMedia('(max-width: 767px)');

  function setState(tab, isOpen) {
    var panel = document.getElementById(tab.getAttribute('aria-controls'));

    tab.classList.toggle('is-active', isOpen);
    tab.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (panel) {
      panel.classList.toggle('is-active', isOpen);
      panel.hidden = !isOpen;
    }
  }

  function tabsIn(group) {
    return Array.prototype.slice.call(group.querySelectorAll('[data-mc-tab]'));
  }

  function open(group, tab) {
    // On a phone each tab is independent: closing the one you just
    // read to open another is the behaviour people complain about.
    // In the strip exactly one panel can be shown, so it is exclusive.
    if (collapsed.matches) {
      setState(tab, tab.getAttribute('aria-expanded') !== 'true');
      return;
    }

    tabsIn(group).forEach(function (other) {
      setState(other, other === tab);
    });
  }

  /* Applied on load and whenever the breakpoint is crossed - not on
     every resize, so a tab someone opened by hand survives a window
     drag. */
  function applyViewport(group) {
    var tabs = tabsIn(group);
    if (!tabs.length) return;

    if (collapsed.matches) {
      tabs.forEach(function (tab) {
        setState(tab, tab.dataset.openMobile === 'true');
      });
      return;
    }

    var active = group.querySelector('[data-mc-tab][aria-expanded="true"]') || tabs[0];
    tabs.forEach(function (tab) {
      setState(tab, tab === active);
    });
  }

  /* ── binding ───────────────────────────────────────────────
     Delegated at the document, not bound to each tab container. A
     container listener only survives as long as the container does,
     and the theme replaces the product form on every variant change;
     nothing can be swapped out from under the document.
     ─────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (event) {
    var tab = event.target.closest && event.target.closest('[data-mc-tab]');
    if (!tab) return;

    var group = tab.closest('[data-mc-tabs]');
    if (!group) return;

    event.preventDefault();
    open(group, tab);
  });

  /* Arrow keys walk the strip, which is what anyone who has met a tab
     strip before will try. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    var tab = event.target.closest && event.target.closest('[data-mc-tab]');
    if (!tab) return;

    var group = tab.closest('[data-mc-tabs]');
    if (!group) return;

    event.preventDefault();

    var tabs = tabsIn(group);
    var step = event.key === 'ArrowRight' ? 1 : -1;
    var next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];

    next.focus();
    open(group, next);
  });

  /* ── reserved height ───────────────────────────────────────
     Switching tabs used to change the height of the grid row the
     tabs live in, and the buy box spans that row - so a shorter
     panel pulled the whole right-hand column up and the page jumped
     under the reader.

     The fix is to stop the height from changing at all: measure
     every panel once, reserve the tallest, and let the shorter ones
     sit inside that space. The reservation lives on the tab group,
     so nothing outside this block is involved.

     Strip only. On a phone the panels are an accordion where
     changing height is the entire point.
     ─────────────────────────────────────────────────────────── */

  function reserveHeight(group) {
    if (collapsed.matches) {
      group.style.removeProperty('--mc-tabs-h');
      return;
    }

    var panels = group.querySelectorAll('[data-mc-panel]');
    var tallest = 0;

    // Drop the previous reservation first. Measuring a panel that is
    // already holding a min-height just measures that reservation
    // back, so it could never shrink - and never correct itself.
    group.style.setProperty('--mc-tabs-h', '0px');

    // Measured out of flow, so a hidden panel can be sized without
    // it appearing or disturbing the panel currently on screen.
    Array.prototype.forEach.call(panels, function (panel) {
      var wasHidden = panel.hidden;

      if (wasHidden) {
        panel.hidden = false;
        panel.style.position = 'absolute';
        panel.style.visibility = 'hidden';
        panel.style.width = '100%';
      }

      tallest = Math.max(tallest, panel.offsetHeight);

      if (wasHidden) {
        panel.style.position = '';
        panel.style.visibility = '';
        panel.style.width = '';
        panel.hidden = true;
      }
    });

    group.style.setProperty('--mc-tabs-h', Math.ceil(tallest) + 'px');
  }

  /* Re-measured on width changes only. Height changes are this
     function's own output, and reacting to them would be a loop. */
  function watchWidth(group) {
    if (!window.ResizeObserver || group.dataset.mcTabsWatched === 'true') return;
    group.dataset.mcTabsWatched = 'true';

    var lastWidth = 0;

    new ResizeObserver(function (entries) {
      var width = Math.round(entries[0].contentRect.width);
      if (width === lastWidth) return;

      lastWidth = width;
      reserveHeight(group);
    }).observe(group);
  }

  /* #mc-tab-<id> in the address bar opens that tab, so the assurance
     panel and the footer can link straight to shipping terms. */
  function openFromHash(group) {
    if (!window.location.hash) return;

    var target = group.querySelector(window.location.hash + '[data-mc-tab]');
    if (target) {
      // Deliberately setState rather than open(): a link asking for a
      // tab means open it, not toggle whatever it currently is.
      setState(target, true);
      if (!collapsed.matches) {
        tabsIn(group).forEach(function (other) {
          if (other !== target) setState(other, false);
        });
      }
      target.scrollIntoView({ block: 'center' });
    }
  }

  function eachGroup(callback) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mc-tabs]'), callback);
  }

  /* Re-measured whenever the content could have changed size.

     This is what was wrong on first load. `init` still called a
     `bind` that no longer existed, so it threw before it ever
     reserved a height - and the only thing that measured afterwards
     was the breakpoint handler, which is why a trip through the
     responsive view fixed it and a plain reload did not.

     The other half is timing: at DOMContentLoaded the text is still
     in the fallback font and the grid has not been laid out, so a
     panel measured then is measured wrong. fonts.ready, load and the
     next frame each cover one of those. */
  function remeasure() {
    eachGroup(reserveHeight);
  }

  function init() {
    eachGroup(function (group) {
      applyViewport(group);
      openFromHash(group);
      watchWidth(group);
    });

    window.requestAnimationFrame(remeasure);
  }

  window.addEventListener('load', remeasure);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(remeasure);
  }

  function onBreakpoint() {
    eachGroup(function (group) {
      applyViewport(group);
      reserveHeight(group);
    });
  }

  if (collapsed.addEventListener) {
    collapsed.addEventListener('change', onBreakpoint);
  } else if (collapsed.addListener) {
    // addListener is the deprecated spelling, kept for older Safari.
    collapsed.addListener(onBreakpoint);
  }

  window.addEventListener('hashchange', init);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ═══════════════════════════════════════════════════════════════
   Variant change broadcast

   The theme's VariantSelector (assets/main.js) updates the variant
   on selection by assigning straight to the input:

       form_inputs[i].value = this.currentVariant.id;

   Assigning `.value` in script fires no event, and the follow-up
   updateProductDetails() only swaps a fixed list of elements -
   price, buy buttons, radios, sku, media, share. Anything else on
   the page is never told the variant moved.

   That is why the subscription widget keeps showing the price of
   whichever variant the page loaded with: it is an app block, so
   it is outside the swap list, and it never receives an event it
   could listen for either.

   This listener sits at the document, so it runs after the
   selector's own handler has already written the new id. It then
   fires the events third-party widgets actually listen for.
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function broadcast(event) {
    var selector = event.target.closest && event.target.closest('variant-selector');
    if (!selector) return;

    var scope = selector.closest('.product-form-wrapper') || document;
    var input = scope.querySelector('input[name="id"]');
    if (!input || !input.value) return;

    var variantId = input.value;

    /* Native events first. Most subscription and bundling widgets
       bind to change/input on the variant input, which is exactly
       the event the theme's direct assignment skips. */
    scope.querySelectorAll('input[name="id"]').forEach(function (el) {
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });

    /* Then the custom events, for widgets that listen on the
       document rather than the field. Sent under several names
       because there is no single convention. */
    var detail = { variantId: variantId, id: variantId, sectionId: selector.dataset.section };

    ['variant:change', 'variant:changed', 'variantChange', 'product:variant-change'].forEach(
      function (name) {
        document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
      }
    );
  }

  document.addEventListener('change', function (event) {
    try {
      broadcast(event);
    } catch (err) {
      /* A third-party listener throwing must not take the variant
         picker down with it. */
      if (window.console && console.warn) console.warn('[medicore] variant broadcast failed', err);
    }
  });
})();

/* ═══════════════════════════════════════════════════════════════
   Availability across a variant change

   Two things the theme's own updater gets wrong for this feature,
   both for the same reason: VariantSelector.updateProductDetails()
   in main.js refreshes a fixed list of elements and hardcodes the
   buy-button wording.

     1. The availability pill is not on its list, so it keeps
        showing the state of whichever variant the page loaded with.
     2. Every variant change sets the add-to-cart label back to
        window.variantStrings.addToCart, wiping the backorder
        wording a moment after Liquid rendered it.

   Both are fixed from data already on the page. mc-availability
   renders a map of every variant's pill and button label, so this
   runs synchronously - there is no second request to lose a race
   against, and the pill cannot disagree with the button.
   ═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  function mapFor(scope) {
    var el = scope.querySelector('script[type="application/json"][data-mc-availability-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      if (window.console && console.warn) console.warn('[medicore] availability map unreadable', err);
      return null;
    }
  }

  function apply(section, variantId) {
    var entry = null;
    var map = mapFor(section);
    if (map) entry = map[String(variantId)];
    if (!entry) return;

    /* Replace the pill itself, not its wrapper: the map lives in the
       same wrapper, and overwriting that would throw away the answers
       for every other variant. */
    var pill = section.querySelector('.mc-avail');
    if (pill) pill.outerHTML = entry.html;

    var buyButtons = section.querySelector('.buy-buttons[data-availibility]');
    if (buyButtons) buyButtons.setAttribute('data-mc-state', entry.state);

    /* The dispatch promise is about stock the store is holding. On a
       variant it does not hold, "ships within 24 hours" and an
       arrival date three days out contradict the pill directly above
       them, so the row goes away rather than lying. */
    var dispatch = section.querySelector('[data-mc-dispatch]');
    if (dispatch) dispatch.hidden = entry.state !== 'in';

    /* The label, last and unconditionally: the theme's updater is
       asynchronous, so it will overwrite this once its fetch lands.
       watch() below puts it back. */
    setLabel(section, entry.cta);
  }

  function setLabel(section, label) {
    var button = section.querySelector('button[name="add"]');
    if (!button || button.disabled) return;
    if (button.innerHTML !== label) button.innerHTML = label;
  }

  /* The theme rewrites the button after its fetch resolves. Rather
     than guess how long that takes, watch the button and correct it
     whenever it changes - the guard in setLabel stops the observer
     from re-triggering itself. */
  function watch(section) {
    var button = section.querySelector('button[name="add"]');
    var buyButtons = section.querySelector('.buy-buttons[data-availibility]');
    if (!button || !buyButtons || button.dataset.mcWatched) return;
    button.dataset.mcWatched = 'true';

    new MutationObserver(function () {
      if (buyButtons.getAttribute('data-mc-state') !== 'backorder') return;
      if (button.disabled) return;

      var map = mapFor(section);
      var input = section.querySelector('input[name="id"]');
      var entry = map && input ? map[String(input.value)] : null;
      if (entry) setLabel(section, entry.cta);
    }).observe(button, { childList: true, characterData: true, subtree: true });
  }

  document.addEventListener('change', function (event) {
    var selector = event.target.closest && event.target.closest('variant-selector');
    if (!selector) return;

    var section = selector.closest('.product-detail');
    if (!section) return;

    var input = section.querySelector('input[name="id"]');
    if (!input || !input.value) return;

    try {
      watch(section);
      apply(section, input.value);
    } catch (err) {
      if (window.console && console.warn) console.warn('[medicore] availability update failed', err);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.product-detail').forEach(watch);
  });
})();
